// ** React Imports
import { useEffect } from "react";
import { generatePath, matchPath, useLocation } from "react-router-dom";

// ** Redux & Store & Actions
import { connect, useSelector } from "react-redux";
import { getProfileAlbums } from "store/modules/user";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { renderRoutes } from "react-router-config";
import styled from "styled-components";
import { breakpoints } from "styles/grid";

// ** Custom Components
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardBody } from "components/layout/Card/CardBody";
import { CardTitle } from "components/layout/Card/CardTitle";
import { CardsBlockWrapper } from "components/layout/Card/CardsBlockWrapper";
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import Tooltip from "components/layout/Tooltip";
import Loader, { StyledLoaderWrapper } from "components/layout/Loader";
import NavImageAlbumLink from "containers/Userpanel/containers/GalleryAndPosts/components/NavImageAlbumLink";

// ** Images Components
import Album from "./components/Album";

// #####################################################

const GridWrapper = styled.div`
	display: grid;
	gap: var(--row-gap) var(--column-gap);

	// 1340+
	grid-template-columns: repeat(8, minmax(0, 1fr));
	// 1050-1339
	@media (max-width: ${breakpoints.giant - 1}px) {
		grid-template-columns: repeat(6, minmax(0, 1fr));
	}
	// 600-1049
	@media (max-width: ${breakpoints.desktop - 1}px) {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}
	// 0-599
	@media (max-width: ${breakpoints.phone - 1}px) {
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: var(--row-gap-mobile) var(--column-gap-mobile);
		margin: 0 3px;
	}
	/* grid-template-columns: repeat(auto-fill, 116px); */
	/* grid-template-columns: repeat(auto-fill, minmax(116px)); */
	width: 100%;
`;

// #####################################################

const Galleries = ({
	getProfileAlbums,
	match: {
		params: { login },
	},
	route: { path: userGalleryPath },
}) => {
	const user = useSelector((state) => state.userProfileMain);

	const albums_list = useSelector(
		(state) => state.user.galleries.albums_list
	);
	const globalUserId = useSelector((state) => state.globalUser.id);
	const language = useSelector((state) => state.globalCommon.language);

	const isAuth = useSelector((state) => state.globalCommon.isAuth);
	const { t } = useTranslation(["userProfile", "pages"]);
	const location = useLocation();

	const isMe = user.id === globalUserId;
	const pathname = location?.pathname ?? "";

	const allowPrivateMedia =
		user.status.friendStatus === 3 || isMe ? true : null;

	useEffect(() => {
		if (user.loaded) {
			getProfileAlbums(user.id, isAuth, allowPrivateMedia);
		}
	}, [user.loaded, language]);

	const routes = Array.isArray(albums_list?.data)
		? albums_list.data.map((album) => ({
				component: Album,
				path: `${userGalleryPath}/album/${album?.id}`,
				albumId: album?.id,
				albumPrivate: album?.private,
				amount: album?.amount,
		  }))
		: [];

	const albumListFiltered = albums_list.data.filter((album) => {
		return album?.amount > 0;
	});

	let isSomeActive = user.status.friendStatus === 3 || isMe;

	if (!isSomeActive) {
		albumListFiltered.map((album) => {
			if (!album.private) {
				isSomeActive = true;
				return true;
			}
		});
	}

	/**
	 * Dodanie głównej ścieżki 'users/:login/images'
	 * do renderowania,
	 */
	let defaultIndex = null;

	if (albumListFiltered.length) {
		albums_list.data.find((album, index) => {
			if (album.id === albumListFiltered[0].id) {
				defaultIndex = index;
				return;
			}
		});

		routes.push({
			component: Album,
			exact: true,
			path: userGalleryPath,
			mainRoute: true,
			albumId: albumListFiltered[0]?.id,
			albumPrivate: albumListFiltered[0]?.private,
		});
	}

	const avatarDefaultType =
		user.sex === "transsexual"
			? "t"
			: user.sex === "couple"
			? "p"
			: user.sex === "woman"
			? "k"
			: "m";

	const defaultAvatar = `https://imgcdn.datezone.com/static/img/layout/avatar/avatar_${avatarDefaultType}_big.jpg?v=2`;

	// #####################################################

	return (
		<CardsBlockWrapper>
			<CardWrapper>
				{albums_list.loaded && albums_list.data.length > 0 && (
					<CardTitle>
						<Text bold>{t("pages:userpanelAlbums")}</Text>
					</CardTitle>
				)}
				<CardBody noPaddingXmobile={albums_list.data.length > 0}>
					{albums_list.loading && (
						<StyledLoaderWrapper>
							<Loader />
						</StyledLoaderWrapper>
					)}

					{albums_list.loaded &&
						!albums_list.loading &&
						albums_list.data.length === 0 && (
							<Block mt={2} my={2}>
								<Text>{t("userProfile:noGalleries")}</Text>
							</Block>
						)}
					{albums_list.loaded && albums_list.data.length > 0 && (
						<Block flex alignBaseline>
							<GridWrapper>
								{albums_list.data.map((route, index) => {
									if (route.amount === 0) {
										return null;
									}

									const to = generatePath(
										`${userGalleryPath}/album/:album`,
										{
											login: login?.toLowerCase(),
											album: route.id,
										}
									);

									const isDisabled =
										route.private &&
										user.status.friendStatus !== 3 &&
										!isMe;

									const isActive =
										!isDisabled &&
										matchPath(pathname, {
											path: userGalleryPath,
											exact: true,
										}) &&
										index === defaultIndex
											? true
											: !isDisabled &&
											  matchPath(pathname, {
													path: `${userGalleryPath}/album/${route.id}`,
											  });

									return (
										<Tooltip
											text={
												route.private &&
												user.status.friendStatus !==
													3 &&
												!isMe
													? t(
															"userProfile:tooltip:privateGallery"
													  )
													: ""
											}
											key={`albums-tooltip-${route.id}`}
										>
											<NavImageAlbumLink
												key={route.key}
												to={to}
												album={route}
												isActive={isActive}
												isDisabled={isDisabled}
												isAuth={isAuth}
												defaultAvatar={defaultAvatar}
												user={user}
												isMe={isMe}
											/>
										</Tooltip>
									);
								})}
							</GridWrapper>
						</Block>
					)}
				</CardBody>
			</CardWrapper>
			{albums_list.loaded &&
				albums_list.data.length > 0 &&
				isSomeActive && (
					<CardWrapper>
						<CardBody $customPaddingInMobile={"0.75rem 3px"}>
							{renderRoutes(routes)}
						</CardBody>
					</CardWrapper>
				)}
		</CardsBlockWrapper>
	);
};

// #####################################################

export default connect(null, {
	getProfileAlbums,
})(Galleries);

// #####################################################
